import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Client, Conversation, Paginator } from '@twilio/conversations';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ChatService {
  private _chatApi = `${environment.chat}`;
  private client: Client | null = null;

  constructor(private http: HttpClient) { }

  getChatConversationId(sid: string): Observable<any> {
    return this.http.get<any>(`${this._chatApi}/GetChatConversationPair/${sid}`);
  }

  updateChatList(user: any): Observable<any> {
    return this.http.put<any>(`${this._chatApi}/UpdateChatList`, user);
  }

  CreateChatConverationPair(email1: string, email2: string): Observable<any> {
    return this.http.put<any>(`${this._chatApi}/CreateChatConversationPairs?email1=${email1}&email2=${email2}`, {});
  }


  getChatConversations(): Observable<any> {
    return this.http.get<any>(`${this._chatApi}/GetChatList`);
  }

  getAllContacts(): Observable<any> {
    return this.http.get<any>(`${this._chatApi}/GetAllContactList`);
  }

  async getAccessToken(identity: any): Promise<string> {
    const response: any = await this.http.get(`${this._chatApi}/GenerateTwilioToken/${identity}`).toPromise();
    return response.token;
  }

  async initializeClient(token: string): Promise<void> {
    if (this.client) {
      return;
    }

    try {
      this.client = new Client(token);
    } catch (error) {
      console.error("Error initializing Twilio client:", error);
    }
  }

  async createOrGetConversation(user1: string, user2: string): Promise<string> {
    const uniqueName = `conv_${user1}_${user2}`;
    if (!this.client) throw new Error('Client is not initialized');

    try {
      // Check if the conversation already exists by unique name
      const conversation = await this.client.getConversationByUniqueName(uniqueName);
      return conversation.sid;
    } catch (error) {
      const newConversation = await this.client.createConversation({ uniqueName });
      await newConversation.add(user1); // Add User 1
      await newConversation.add(user2); // Add User 2
      return newConversation.sid;
    }
  }

  async getConversationById(conversationId: string): Promise<Conversation | undefined> {
    if (!this.client) {
      throw new Error("Twilio client is not initialized");
    }
    try {
      return await this.client.getConversationBySid(conversationId);
    } catch (error) {
      console.error("Error retrieving conversation by ID:", error);
      return undefined;
    }
  }

  getUserConversations(): Promise<Conversation[]> {
    return new Promise<Conversation[]>(async (resolve, reject) => {
      try {
        const paginator = await this.client?.getSubscribedConversations();

        // If paginator is undefined, reject with an error message
        if (!paginator) {
          reject('No conversations found');
          return;
        }

        resolve(paginator.items);
      } catch (error) {
        reject('Can not get user conversations');
      }
    });
  }


  async sendMessage(conversationId: string, message: string): Promise<void> {
    if (!this.client) {
      throw new Error("Twilio client is not initialized");
    }
    try {
      const conversation = await this.client?.getConversationBySid(conversationId);
      await conversation?.sendMessage(message);
    } catch (error) {
      console.error("Error retrieving conversation by ID:", error);
      return undefined;
    }

  }

  listenForMessages(callback: (message: any) => void): void {
    this.client?.on('messageAdded', callback);
  }
}
